/* eslint-disable no-await-in-loop */
import JSZip from 'jszip';
import { PDFDocument } from 'pdf-lib';

export default class EntitiesDocumentsDownloader {

    static ENTITY_TYPES = {
        ACCOUNT: 'account',
        OPERATION: 'operation',
        INTERVENER: 'intervener'
    };

    static MODE_TYPES = {
        GROUPED: 'grouped',
        COMPRESSED: 'compressed'
    };

    constructor(vueInstance, mode, entityType) {
        if (!Object.values(EntitiesDocumentsDownloader.ENTITY_TYPES).includes(entityType)) {
            throw new Error('Invalid entity type');
        }
        if (!Object.values(EntitiesDocumentsDownloader.MODE_TYPES).includes(mode)) {
            throw new Error('Invalid mode');
        }
        const entityToDocumentsValidationsMethodMap = {
            account: vueInstance.$accountsService.getAccountDocumentsValidations,
            operation: vueInstance.$operationsService.getDocumentsValidations,
            intervener: vueInstance.$intervenersService.getIntervenerDocumentsValidations
        };
        const entityToDocumentsDownloadMethodMap = {
            account: vueInstance.$accountsService.downloadAccountDocument,
            operation: vueInstance.$operationsService.downloadOperationDocument,
            intervener: vueInstance.$intervenersService.downloadIntervenerDocument
        };
        const modeToMethodMap = {
            grouped:  this.groupDocuments,
            compressed: this.compressDocuments
        };
        this.vueInstance = vueInstance;
        this.entityType = entityType;
        this.getDocumentsValidations = entityToDocumentsValidationsMethodMap[entityType];
        this.downloadFiles = entityToDocumentsDownloadMethodMap[entityType];
        this.formatFiles = modeToMethodMap[mode];
        this.entityUuidProperty = `uuid_${entityType}`;
    }

    getEntityUuidToIdObjectMap(entities) {
        const uuidToIdObjectMap = new Map();
        entities.forEach(entity => uuidToIdObjectMap.set(entity.uuid, entity.id_object));
        return uuidToIdObjectMap;
    }

    async download(entities) {
        const entityUuidToFilesMap = await this.downloadEntitiesFiles(entities);
    
        if (entityUuidToFilesMap.size === 0) {
            this.vueInstance.$toast.warning('Aviso', 'No hay documentos disponibles para descargar');
            return;
        }
    
        const zip = new JSZip();
        for (const [entityUuid, files] of entityUuidToFilesMap) {
            if (!files || files.length === 0) continue;
    
            for (let index = 0; index < files.length; index++) {
                const file = files[index];
                
                try {
                    const fileData = await new Response(file.data).arrayBuffer();
                    let fileExtension = this.getFileExtensionFromMimeType(file.headers['content-type']);
                    const fileName = file.title
                        ? `${file.title}.${fileExtension}`
                        : `${entityUuid}_documento_${index + 1}.${fileExtension}`;
    
                    zip.file(fileName, fileData);
                } catch (error) {
                    console.error(`Error al procesar el archivo ${index + 1} de ${entityUuid}:`, error);
                }
            }
        }
    
        if (zip.files && Object.keys(zip.files).length > 0) {
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            this.downloadFile(zipBlob, `documentos_${Date.now()}.zip`);
        } else {
            this.vueInstance.$toast.warning('Aviso', 'No hay documentos para descargar');
        }
    }
    
    /**
     * Obtener la extensión del archivo a partir del "content-type"
     * @param {string} mimeType El tipo de contenido recibido
     * @returns {string} La extensión del archivo (como "pdf", "jpeg", etc.)
     */
    getFileExtensionFromMimeType(mimeType) {
        if (mimeType.includes('pdf')) return 'pdf';
        if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpeg';
        if (mimeType.includes('png')) return 'png';
        return null;
    }

    async downloadEntitiesFiles(entities) {
        const entitiesDocuments = await this.getEntitiesDocumentsValidations(entities);
        const entitiesFilesPromises = entitiesDocuments.map(document =>
            this.downloadFiles(document[this.entityUuidProperty], document.uuid)
        );
        const entitiesFiles = await Promise.all(entitiesFilesPromises);
        const entityIdToFilesMap = this.groupFilesByEntity(
            entitiesFiles, entitiesDocuments, this.entityUuidProperty
        );
        if (this.entityType === EntitiesDocumentsDownloader.ENTITY_TYPES.ACCOUNT) {
            await this.addIntervenersFilesToAccountsFiles(entities, entityIdToFilesMap);
            await this.addOperationsFilesToAccountsFiles(entities, entityIdToFilesMap);
        }
        return entityIdToFilesMap;
    }
    /**
     *  Se obtienen todos los documentos de la entidad y se filtran para que sean:
     *  Del tipo pdf y los requeridos firmables.
     * @param {*} entities entidad a descargar documentos
     * @return {[]} Array con los documentos requeridos
     */
    async getEntitiesDocumentsValidations(entities) {
        const promises = entities.map(entity => this.getDocumentsValidations(entity.uuid));
        const responses = await Promise.all(promises);
        let documentsValidations = [];
        const entityTypeToDocsPropertyMap = {
            account: 'accounts_documents',
            operation: 'operation_documents',
            intervener: 'interveners_documents'
        };
        const docsProperty = entityTypeToDocsPropertyMap[this.entityType];
        responses.forEach(response => {
            const documents = response.data[docsProperty]
                .filter(doc => doc.title && doc.uuid && doc.is_signable && doc.mimetype !== 'video/mp4')
            
            documentsValidations = [...documentsValidations, ...documents];
        });
        return documentsValidations;
    }

    groupFilesByEntity(files, documents, entityIdProperty) {
        const entityToFilesMap = new Map();
        files.forEach((file, index) => {
            const document = documents[index];
            const entityId = document[entityIdProperty];
            this.mergeValuesIntoMap(entityToFilesMap, entityId, [file]);
        });
        return entityToFilesMap;
    }

    async addIntervenersFilesToAccountsFiles(accounts, accountToFilesMap) {
        const intervenerToFilesMaps = await this.downloadAccountsIntervenersDocuments(accounts);
        intervenerToFilesMaps.forEach((intervenerToFilesMap, index) => {
            const account = accounts[index];
            const intervenersFiles = Array.from(intervenerToFilesMap.values());
            // mergeamos el array de arrays en uno solo
            const accountIntervenersFiles = [].concat(...intervenersFiles);
            this.mergeValuesIntoMap(
                accountToFilesMap,
                account.uuid,
                accountIntervenersFiles
            );
        });
    }

    async addOperationsFilesToAccountsFiles(accounts, accountToFilesMap) {

        for (const account of accounts) {
            const res = await this.vueInstance.$accountsService.getAccountOperations(account.uuid);
            const accountOperations = res.data.related_operations.filter( operation => !['VALIDATED', 'RD_SENT', 'FINALIZED'].includes(operation.operation_status));
            for (const operation of accountOperations) {
                const document = await this.getOperationDocuments(operation.operation_code);
                if (document.length > 0) {
                    this.mergeValuesIntoMap(accountToFilesMap, account.uuid, document);
                }
            }
        }
    }


    async getOperationDocuments(operationUuid) {
        const operationDocuments = [];
        try {
            const response = await this.vueInstance.$operationsService.getDocumentsValidations(operationUuid);
            const documents = response.data.operation_documents
                .filter(doc => doc.uuid)
                .filter(doc => doc.is_signable && doc.mimetype === 'application/pdf');

            for (const document of documents) {
                const downloaded = await this.vueInstance.$operationsService.downloadOperationDocument(
                    document.uuid_operation,
                    document.uuid
                );
                downloaded.title = document.key_subcategory;
                operationDocuments.push(downloaded);
            }

            return operationDocuments;
        } catch (err) {
            throw new Error(`downloadOperationDocuments: ${err}`);
        }
    }

    downloadAccountsIntervenersDocuments(accounts) {
        const entitiesDocumentsDownloader = new EntitiesDocumentsDownloader(
            this.vueInstance, 'grouped', EntitiesDocumentsDownloader.ENTITY_TYPES.INTERVENER
        );
        const intervenerFilesPromises = [];
        accounts.forEach(account => {
            const promise = entitiesDocumentsDownloader.downloadEntitiesFiles(
                account.interveners
            );
            intervenerFilesPromises.push(promise);
        });
        return Promise.all(intervenerFilesPromises);
    }

    mergeValuesIntoMap(map, key, newValues) {
        if (map.has(key)) {
            const currentValues = map.get(key);
            map.set(key, [...currentValues, ...newValues]);
        } else {
            map.set(key, newValues);
        }
    }

    compressDocuments(documents, entityId) {
        const zip = new JSZip();
        documents.forEach( document => {
            zip.file(`${entityId}_${new Date()}.pdf`, document, { base64: true });
        });
        return { file: zip.generateAsync({ type:'blob' }), title: entityId };
    }

    async groupDocuments(documents, entityId) {
        const mergedPdf = await PDFDocument.create();
        const docsBytesPromises = [];
        const docsPdfsPromises = [];
        const pdfsPagesPromises = [];

        documents.forEach(document => {
            const docBytesPromise = new Response(document.data).arrayBuffer();
            docsBytesPromises.push(docBytesPromise);
        });
        const docsBytes = await Promise.all(docsBytesPromises);
        docsBytes.forEach(docBytes => {
            const docPdfPromise = PDFDocument.load(docBytes);
            docsPdfsPromises.push(docPdfPromise);
        });
        const docsPdfs = await Promise.all(docsPdfsPromises);
        docsPdfs.forEach(pdf => {
            const pdfPagesPromise = mergedPdf.copyPages(pdf, pdf.getPageIndices());
            pdfsPagesPromises.push(pdfPagesPromise);
        });
        const pdfsPages = await Promise.all(pdfsPagesPromises);
        pdfsPages.forEach(pdfPages =>
            pdfPages.forEach(page => mergedPdf.addPage(page))
        );
        return { file: new Blob([await mergedPdf.save()]), title: `${entityId}.pdf` };
    }

    downloadFile( content, title ) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = title;
        document.body.append(link);
        link.click();
        link.remove();
        window.addEventListener('focus', () => URL.revokeObjectURL(link.href), { once: true });
    }
}
